import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Container, Row, Col, Alert, InputGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { FaUserAlt, FaLock, FaSignInAlt } from 'react-icons/fa';

// Login validation schema
const loginSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
});

const Login = () => {
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  // Single secure credential for authentication
  // These should ideally be environment variables in a production app
  // Using a secure hashed password would be even better
  const ADMIN_USERNAME = process.env.REACT_APP_ADMIN_USERNAME || 'admin';
  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'SRM@admin2025#';
  
  // Single valid credential - no other users can access
  const validCredentials = {
    username: ADMIN_USERNAME,
    password: ADMIN_PASSWORD
  };
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = (values, { setSubmitting }) => {
    setError('');
    
    // Simple authentication check
    if (values.username === validCredentials.username && 
        values.password === validCredentials.password) {
      // Use the login function from AuthContext
      login({ username: values.username });
      
      // Show success toast
      toast.success('Login successful! Welcome to the Client Management System');
      
      // Redirect to client list or the page they were trying to access
      const from = location.state?.from?.pathname || '/';
      navigate(from);
    } else {
      setError('Invalid username or password');
      toast.error('Invalid username or password');
    }
    
    setSubmitting(false);
  };

  return (
    <Container fluid className="login-container d-flex align-items-center justify-content-center vh-100">
      <div className="position-absolute top-0 start-0 w-100 h-100 login-bg-overlay"></div>
      <Row className="justify-content-center w-100 position-relative z-index-1">
        <Col md={6} lg={5} xl={4}>
          <Card className="login-card border-0">
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <div className="brand-icon mx-auto d-flex align-items-center justify-content-center mb-3">
                  <FaUserAlt />
                </div>
                <h2 className="section-title mb-1">Client <span className="highlight-text">Manager</span></h2>
                <p className="text-muted">Secure Admin Portal</p>
              </div>
              
              {error && (
                <Alert variant="danger" className="d-flex align-items-center p-3 mb-4">
                  <div className="me-3 fs-5">
                    <FaLock />
                  </div>
                  <div>{error}</div>
                </Alert>
              )}
              
              <Formik
                initialValues={{ username: '', password: '' }}
                validationSchema={loginSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <Form onSubmit={handleSubmit} className="login-form">
                    <Form.Group className="mb-4">
                      <Form.Label className="filter-label">Username</Form.Label>
                      <InputGroup className="filter-input">
                        <InputGroup.Text>
                          <FaUserAlt />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          name="username"
                          className="search-control"
                          placeholder="Enter your username"
                          value={values.username}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.username && errors.username}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.username}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                    
                    <Form.Group className="mb-5">
                      <Form.Label className="filter-label">Password</Form.Label>
                      <InputGroup className="filter-input">
                        <InputGroup.Text>
                          <FaLock />
                        </InputGroup.Text>
                        <Form.Control
                          type="password"
                          name="password"
                          className="search-control"
                          placeholder="Enter your password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.password && errors.password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.password}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                    
                    <div className="d-grid">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-glow py-3"
                      >
                        <span className="btn-text-wrapper d-flex align-items-center justify-content-center">
                          {isSubmitting ? 'Authenticating...' : (
                            <>
                              <FaSignInAlt className="me-2" />
                              <span>Secure Login</span>
                            </>
                          )}
                        </span>
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
              
              <div className="text-center mt-4">
                <small className="text-muted d-block">
                  Secure Admin Access Only
                </small>
                <small className="text-muted d-block mt-1">
                  © 2025Client Management System
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;
